import {
  initProStudioUrqlGql,
  SolutionEnvironmentsEnum,
} from '@pypestream/api-services/urql';
import { reportError, throwErrorWithId } from '@pypestream/utils';
import { capitalize } from 'lodash';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import {
  buildVersionInput,
  updateSurveyBotStreams,
  UpdateSurveyBotStreamsParams,
} from './helpers/solutions';
import { checkForErrors } from '../../utils/check-for-errors';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  solutionAnalyticsProps,
  splitSupportErrorId,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
} from '../../utils/analytics';

/**
 * Where a publish attempt died. `solution_published + solution_published_failed`
 * is only the number of publish attempts if EVERY entrance reports, so each
 * failure exit of this invoke carries one of these.
 *
 * NOT covered, and deliberately so: the two pre-flight refusals in
 * `publish-modal.tsx` — a rejected `waitForPendingSaves` and a
 * `resolvePublishBlock` hit (a duplicate or missing node number, the most
 * frequent publish refusal in this code). Both show a publish-failed toast and
 * `return` without ever dispatching `publishBot`, so this invoke cannot see
 * them; instrumenting them means rebuilding the identity payload in a
 * component that has none of it assembled. They are a separate change, and
 * until it lands the failure counts here under-report by those two classes.
 */
type PublishFailureStage =
  | 'guard'
  | 'survey_streams'
  | 'mutation'
  | 'publish_response';

export const publishBotVersion = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<void> => {
  if (event.type !== 'publishBot') {
    return;
  }

  const {
    botName: ctxBotName,
    customerId,
    project,
    solutions: bots,
    userInfo,
    draftVersionSolutionData,
    solutionVersions,
    selectedBot,
  } = ctx;

  const projectId = project?.id;

  const {
    version,
    republish,
    env: environment,
    botType,
    bots: eventBots,
  } = event.payload;

  /**
   * The one place a `solution_published_failed` payload is built, so that every
   * failure exit reports the same shape.
   *
   * The identity fields are read defensively because the guards below fire
   * exactly when one of them is missing — an event with `customer_id:
   * undefined` still counts the attempt, which is the whole point.
   *
   * Called BEFORE the `throwErrorWithId` / rethrow beside it rather than after,
   * because those do not return: there is no "after". That is safe here and is
   * not a contradiction of "serve the user first" — `captureAnalyticsEvent`
   * wraps its whole body in `try/catch`, and every argument expression below is
   * a literal, an optional chain or a `String()`, so none of them can throw.
   */
  const captureFailure = (
    failureStage: PublishFailureStage,
    rawMessage: string,
    attemptedVersionId: string | null = null
  ): void => {
    const { message, errorId } = splitSupportErrorId(rawMessage);
    const sentMessage = message.slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH);

    captureAnalyticsEvent('solution_published_failed', {
      ...solutionAnalyticsProps(ctx),
      attempted_version_id: attemptedVersionId,
      publish_environment: environment,
      republish: Boolean(republish),
      failure_stage: failureStage,
      error_kind: classifyAnalyticsError(sentMessage),
      error_message: sentMessage,
      error_id: errorId,
    });
  };

  const toMessage = (error: unknown, fallback: string): string =>
    error instanceof Error ? error.message : fallback;

  if (!projectId) {
    captureFailure('guard', 'Project ID is required');
    throwErrorWithId('Project ID is required');
    return;
  }

  if (
    !userInfo?.id ||
    !userInfo?.email ||
    !userInfo?.firstName ||
    !userInfo?.lastName
  ) {
    captureFailure('guard', 'User info is required');
    throwErrorWithId('User info is required');
    return;
  }

  if (!ctxBotName) {
    captureFailure('guard', 'Bot Name is required');
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    captureFailure('guard', 'Customer ID is required');
    throwErrorWithId('Customer ID is required');
    return;
  }

  const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

  if (!uniqueCustomerId) {
    captureFailure('guard', 'Customer ID is required');
    throwErrorWithId('Customer ID is required');
    return;
  }

  const uniqueSolutionId =
    selectedBot?.uniqueId || solutionVersions?.[0]?.solutionId;

  if (!uniqueSolutionId) {
    captureFailure('guard', 'Solution unique ID is required');
    throwErrorWithId('Solution unique ID is required');
    return;
  }

  if (!version) {
    captureFailure('guard', 'Version is required');
    throwErrorWithId('Version is required');
    return;
  }

  const botId = `${customerId}.${ctxBotName}`;

  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  if (botType === 'survey') {
    try {
      await updateSurveyBotStreams({
        bots,
        eventBots,
        environment,
        uniqueCustomerId,
        projectId,
        botId,
        userInfo: userInfo as UpdateSurveyBotStreamsParams['userInfo'],
      });
    } catch (error) {
      // Rethrown unchanged: this rejection is what the machine's `onError`
      // turns into the user's toast, and the capture must not alter it.
      captureFailure(
        'survey_streams',
        toMessage(error, 'Error updating survey bot streams')
      );
      throw error;
    }
  }

  const botSolutionVersionId = `${botId}.${version}`;

  const getRepublishVersionId = () => {
    const matchingVersion = solutionVersions.find(
      (v) => v?.botSolutionVersionId === botSolutionVersionId
    );
    return matchingVersion?.id;
  };

  const solutionVersionId = republish
    ? getRepublishVersionId()
    : draftVersionSolutionData?.id;

  const basePayload = {
    doVersionBump: !republish,
    environment: capitalize(environment) as SolutionEnvironmentsEnum,
  };

  let publishSolutionVersionResponse;
  let publishedVersionId: string | null = null;

  try {
    if (!solutionVersionId) {
      publishSolutionVersionResponse =
        await urqlGql.createAndPublishSolutionVersion({
          ...basePayload,
          solutionId: uniqueSolutionId,
          version: buildVersionInput(
            botSolutionVersionId,
            draftVersionSolutionData
          ),
        });
      publishedVersionId =
        publishSolutionVersionResponse.data?.admin_
          ?.createAndPublishSolutionVersion?.id ?? null;
    } else {
      publishSolutionVersionResponse = await urqlGql.publishSolutionVersion({
        ...basePayload,
        solutionVersionId,
      });
      publishedVersionId =
        publishSolutionVersionResponse.data?.admin_?.publishSolutionVersion
          ?.id ?? solutionVersionId;
    }
  } catch (error) {
    // A mutation that REJECTS (transport failure, an aborted request) never
    // reaches `checkForErrors` below, so its own catch cannot see it.
    captureFailure(
      'mutation',
      toMessage(error, 'Error publishing solution version'),
      solutionVersionId ?? null
    );
    throw error;
  }

  // The version we attempted — reported back even on error so the modals/watchers
  // can suppress a false success for this version.
  const attemptedVersionId = solutionVersionId ?? publishedVersionId ?? null;

  try {
    checkForErrors(publishSolutionVersionResponse);
  } catch (error) {
    // PSTUDIO-4194 criterion I. This catch is why the publish failure of
    // 2026-09-19 reached the user as a toast and reached Sentry not at all:
    // the error is reduced to a string for the modal and the object is dropped
    // here. The shared `createInvokablePromise` reporter cannot see it either —
    // this invoke RESOLVES, so there is no rejection for `onError` to catch.
    //
    // The `extra` fields are identifiers only — no node content and nothing
    // from the draft. The `error` itself is NOT so bounded, and saying it was
    // would be a false claim about a privacy boundary: its `message` comes from
    // `checkForErrors`, which prefers the backend's own text
    // (`extensions.internal.response.body.errors`) over the generic one, so a
    // constraint violation can carry a value from the row that broke it. What
    // bounds it is the SDK: `extraErrorDataIntegration` is not among the
    // defaults, so `AppError.context` — which holds the whole serialized
    // response — does not travel, and `maxValueLength` truncates the message at
    // 250 characters.
    // Guarded: a throw here would skip `onPublishComplete` below, so the invoke
    // would reject instead of resolving, the modal would never learn the
    // publish failed, and the machine would take its generic error path — the
    // telemetry deciding the user-facing outcome.
    try {
      reportError(error, {
        source: 'publish-bot-version',
        type: 'publish',
        tags: {
          app: 'pro-studio',
          publishEnvironment: basePayload.environment,
        },
        extra: {
          botSolutionVersionId,
          attemptedVersionId,
          republish: Boolean(republish),
        },
      });
    } catch {
      // Reporting is best-effort; the user-facing result below is not.
    }

    const errorMessage = toMessage(error, 'Error publishing solution version');

    captureFailure('publish_response', errorMessage, attemptedVersionId);

    // The modal owns the user-facing toast copy (it has the translation hook).
    event.payload.onPublishComplete({
      status: 'error',
      solutionVersionId: attemptedVersionId,
      errorMessage,
    });
    return;
  }

  captureAnalyticsEvent('solution_published', {
    ...solutionAnalyticsProps(ctx),
    solution_version_id: publishedVersionId,
    publish_environment: environment,
    republish: Boolean(republish),
  });

  event.payload.onPublishComplete({
    status: 'success',
    solutionVersionId: publishedVersionId,
  });
};
